import React, { memo } from 'react';
import { FeatureCardProps } from '../types';
import { Globe, Users, Building2, Compass, Sparkles, MessageSquare } from 'lucide-react';
import { cn } from '../../../utils/cn'; // Assuming you have cn utility

// Memoized feature card component - Further adjustments for mobile
const FeatureCard = memo(({ icon: Icon, title, description }: FeatureCardProps) => (
  <div className="group relative overflow-hidden rounded-lg sm:rounded-xl bg-white/[0.02] hover:bg-white/[0.03] border border-white/[0.05] transition-all duration-300">
    {/* Removed absolute gradient overlay for minimalism */}
    {/* <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div> */}
    <div className="relative p-3 sm:p-4"> {/* Base padding smaller, increases slightly for sm+ */}
      <div className="mb-2 sm:mb-3"> {/* Base margin smaller */}
        {/* Base icon container smaller */}
        <div className="p-1.5 sm:p-2 rounded-md sm:rounded-lg bg-white/[0.04] w-fit">
          {/* Base icon smaller */}
          <Icon className="w-3 h-3 sm:w-4 sm:h-4 text-white/60" />
        </div>
      </div>
      {/* Base title smaller */}
      <h3 className="text-white text-sm sm:text-base font-medium mb-1 sm:mb-1.5">{title}</h3>
      {/* Base description smaller, very tight leading */}
      <p className="text-white/50 text-[11px] sm:text-xs font-light leading-tight sm:leading-snug">{description}</p>
    </div>
  </div>
));
FeatureCard.displayName = 'FeatureCard';

const FeatureSection: React.FC = () => {
  const features = [
    // Descriptions might need further shortening if they wrap too much on mobile
    { icon: Globe, title: 'Interactive Map', description: 'Explore agents & places' },
    { icon: Users, title: 'Expert Agents', description: 'Connect worldwide' },
    { icon: Building2, title: 'Luxury Hotels', description: 'Book premium stays' },
    { icon: Compass, title: 'Curated Tours', description: 'Browse unique trips' },
    { icon: Sparkles, title: 'Travel Expo', description: 'Find exclusive deals' },
    { icon: MessageSquare, title: 'Marco AI', description: 'Get AI travel help' }
  ];

  return (
    // Base vertical padding smaller
    <div className="w-full py-10 sm:py-16 lg:py-20 bg-gradient-to-b from-black via-neutral-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Base bottom margin smaller */}
        <div className="text-center mb-5 sm:mb-10">
          {/* Base heading smaller */}
          <h2 className="text-lg sm:text-3xl font-display font-medium tracking-tight text-white mb-1.5 sm:mb-3">
            Your Complete Travel Platform
          </h2>
          {/* Base paragraph smaller */}
          <p className="text-white/60 text-xs sm:text-base font-light leading-relaxed max-w-[600px] mx-auto">
            Discover all the ways AgenTrav can enhance your travel experience
          </p>
        </div>
        
        {/* Base grid gap smaller */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description} // Using even shorter descriptions
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default FeatureSection; 